/**
 * Lightweight hook for getting due word count.
 *
 * Uses FIRe spaced repetition algorithm (the app's primary SRS system)
 * to count words that are due for review.
 *
 * For full review queue functionality, use useReviewQueue instead.
 */

import { useState, useEffect, useCallback } from 'react';
import { STORAGE_KEYS } from '../lib/storageService';
import { getFIReDueWordCount } from '../lib/progress/progressFIRe';

/**
 * Lightweight hook for getting due word count using FIRe SRS.
 *
 * @returns Object with dueCount and refresh function
 */
export function useDueCount(): { dueCount: number; refresh: () => void } {
  const [dueCount, setDueCount] = useState(() => {
    return getFIReDueWordCount();
  });

  const refresh = useCallback(() => {
    setDueCount(getFIReDueWordCount());
  }, []);

  useEffect(() => {
    // Listen for storage events (cross-tab sync)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEYS.PROGRESS) {
        refresh();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also refresh periodically to catch same-tab updates
    // (localStorage doesn't fire events for same-tab writes)
    const intervalId = setInterval(refresh, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [refresh]);

  return { dueCount, refresh };
}

export default useDueCount;
